import { useState, useRef } from "react";

const W = 300;
const H = 60;
const PAD = { t: 6, r: 4, b: 4, l: 4 };

function formatMonth(str) {
  const [year, month] = str.split("-");
  return new Date(parseInt(year), parseInt(month) - 1, 1)
    .toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function buildPts(data) {
  const ys = data.map((d) => d.y);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const yRange = maxY - minY || 1;
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;

  return data.map((d, i) => ({
    ...d,
    sx: PAD.l + (i / (data.length - 1)) * plotW,
    sy: PAD.t + (1 - (d.y - minY) / yRange) * plotH,
  }));
}

function linePath(pts) {
  let d = `M ${pts[0].sx} ${pts[0].sy}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const cpx = (pts[i].sx + pts[i + 1].sx) / 2;
    d += ` C ${cpx} ${pts[i].sy} ${cpx} ${pts[i + 1].sy} ${pts[i + 1].sx} ${pts[i + 1].sy}`;
  }
  return d;
}

function areaPath(pts) {
  return `${linePath(pts)} L ${pts[pts.length - 1].sx} ${H} L ${pts[0].sx} ${H} Z`;
}

export default function Sparkline({ data }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const ref = useRef(null);

  if (!data || data.length < 2) return null;

  const pts = buildPts(data);
  const h = hoveredIdx !== null ? pts[hoveredIdx] : null;
  const leftPct = hoveredIdx !== null ? (hoveredIdx / (pts.length - 1)) * 100 : 50;

  function onMouseMove(e) {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    const idx = Math.max(0, Math.min(pts.length - 1, Math.round(frac * (pts.length - 1))));
    setHoveredIdx(idx);
  }

  return (
    <div
      ref={ref}
      style={{ position: "relative", height: "80px", width: "100%", cursor: "crosshair" }}
      onMouseMove={onMouseMove}
      onMouseLeave={() => setHoveredIdx(null)}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="100%"
        style={{ display: "block", overflow: "visible" }}
      >
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d3963a" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#d3963a" stopOpacity="0" />
          </linearGradient>
        </defs>

        <path d={areaPath(pts)} fill="url(#spark-fill)" />
        <path d={linePath(pts)} fill="none" stroke="#d3963a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {h && (
          <>
            <line x1={h.sx} y1={PAD.t} x2={h.sx} y2={H} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <circle cx={h.sx} cy={h.sy} r="3.5" fill="#d3963a" />
          </>
        )}
      </svg>

      {h && (
        <div style={{
          position: "absolute",
          bottom: "calc(100% - 2px)",
          left: `clamp(0px, ${leftPct}%, calc(100% - 110px))`,
          transform: leftPct > 80 ? "translateX(-100%)" : leftPct < 20 ? "translateX(0)" : "translateX(-50%)",
          background: "#111115",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "6px",
          padding: "4px 9px",
          fontSize: "12px",
          color: "#f0ede8",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          zIndex: 20,
        }}>
          {formatMonth(h.x)} · {h.y}
        </div>
      )}
    </div>
  );
}
